import {goBack} from '@github-ui/history'
import {Link, useParams} from '@github-ui/react-router'
import {type EditableShortcutField, ShortcutEditingHeader, type UserShortcut} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading, TextInput} from '@primer/react'
import {useCallback, useState} from 'react'

import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import styles from './RepositoryViewNewPage.module.css'

const DEFAULT_QUERY_PREFIX = 'is:open is:issue'

function buildDefaultQuery(owner: string, repo: string): string {
  return owner && repo ? `${DEFAULT_QUERY_PREFIX} repo:${owner}/${repo}` : DEFAULT_QUERY_PREFIX
}

function buildInitialShortcut(owner: string, repo: string): UserShortcut {
  return {
    id: '',
    name: '',
    searchType: 'issues',
    query: buildDefaultQuery(owner, repo),
    description: '',
    icon: 'bookmark',
    color: 'gray',
  }
}

export function RepositoryViewNewContent() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()

  const [shortcut, setShortcut] = useState<UserShortcut>(() => buildInitialShortcut(owner, repo))

  const setShortcutValue = useCallback((field: EditableShortcutField, value: string) => {
    setShortcut(prev => ({...prev, [field]: value}))
  }, [])

  const {mutate, isPending, error} = useCreateSavedViewMutation({owner, repo})

  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
      },
      {
        onSuccess: ({savedView}) => {
          navigate(`/${owner}/${repo}/issues/views/${savedView.id}`)
        },
      },
    )
  }, [mutate, navigate, owner, repo, shortcut])

  return (
    <div className={styles.middlePaneWrapper} data-hpc>
      <Link to={viewsPath} className={styles.backLink}>
        <ArrowLeftIcon />
        Back to Views
      </Link>
      <Heading as="h1" variant="medium">
        Create view
      </Heading>
      <div className={styles.middlePaneGrid}>
        <ShortcutEditingHeader
          shortcut={shortcut}
          setShortcutValue={setShortcutValue}
          onSave={handleSave}
          onDismiss={goBack}
          isSaving={isPending}
          errorMessage={error?.message}
          renderFilter={({inputRef, className}) => (
            <TextInput
              ref={inputRef}
              className={className}
              value={shortcut.query}
              onChange={event => setShortcutValue('query', event.target.value)}
              placeholder="Search issues"
              aria-label="Query"
            />
          )}
        />
      </div>
    </div>
  )
}
